import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  AppBar,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { CheckCircle, OpenInNew } from "@mui/icons-material";
import Swal from "sweetalert2";
import MainContentContainer from "../../MainContentContainer/MainContentContainer";
import LoadingBackdrop from "../../Common/LoadingBackdrop";
import {
  bulkApproveCaptions,
  fetchCaptionsReviewQueue,
  fetchReviewQueueCaptionIds,
} from "../../../actions/captionsActions";
import { STATUS_COLOR, SEVERITY_COLOR, prettySource } from "../../../constants/captionStyle";

// Status filter options. Order is workflow-driven (queue progression
// top-to-bottom: queued → processing → awaiting review → reviewed /
// edited, with failed as the outlier needing attention). Keep keys
// in sync with App\Enum\CaptionStatus on APIv2 — added "queued" in
// release-2026-05-18-3.
const STATUS_OPTIONS = [
  { value: "all", label: "All (default)" },
  { value: "queued", label: "Queued" },
  { value: "processing", label: "Processing" },
  { value: "auto_pending_review", label: "Awaiting Review" },
  { value: "human_reviewed", label: "Reviewed" },
  { value: "human_edited", label: "Edited" },
  { value: "failed", label: "Failed" },
];

const CaptionsReviewQueuePage = () => {
  const dispatch = useDispatch();
  const reviewQueue = useSelector(state => state.captions.reviewQueue);
  const isLoading = useSelector(state => state.captions.isLoading);

  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0); // MUI page index is 0-based; API is 1-based.
  const [perPage, setPerPage] = useState(25);
  // Caption IDs the operator has selected for bulk approve. Persists
  // across pagination so an operator can click through multiple pages
  // ticking rows then approve them all in one shot. Only rows that
  // are still auto_pending_review at server-time get approved — the
  // backend reports the rest in `skipped` with reason="not_pending_
  // review", which we surface in the result Swal.
  const [selected, setSelected] = useState(() => new Set());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    dispatch(
      fetchCaptionsReviewQueue({
        status: statusFilter,
        page: page + 1,
        per_page: perPage,
      })
    );
  }, [dispatch, statusFilter, page, perPage]);

  const rows = reviewQueue?.data ?? [];
  const totalCount = reviewQueue?.total_count ?? 0;

  // Only auto_pending_review rows are actually approvable. We let the
  // operator tick others (it's harmless — backend skips them with a
  // clear reason), but the page-level header checkbox and the
  // "Select all matching" button focus on the approvable subset so the
  // common workflow (filter=Awaiting Review → click select-all →
  // approve) doesn't fight the operator.
  const approvableRows = useMemo(() => rows.filter(r => r.status === "auto_pending_review"), [rows]);
  const approvableSelectedCount = useMemo(
    () => Array.from(selected).filter(id => approvableRows.some(r => r.session_caption_id === id)).length,
    [selected, approvableRows]
  );

  const isRowSelected = id => selected.has(id);
  const toggleRow = id => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAllOnPage = checked => {
    // Header checkbox scopes to approvable rows on the visible page —
    // not all rows, because ticking already-reviewed/failed rows just
    // adds noise to the selection count without actually approving
    // them (server skips them).
    setSelected(prev => {
      const next = new Set(prev);
      approvableRows.forEach(r => {
        if (checked) next.add(r.session_caption_id);
        else next.delete(r.session_caption_id);
      });
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const onSelectAllMatching = async () => {
    // Single secondary request grabs every caption_id matching the
    // current status filter across all pages. Then add them to the
    // existing selection.
    //
    // Pass `statusFilter` through unmodified — including "all" — so
    // the count on the button label ("Select all {totalCount}
    // matching") matches what actually gets selected. Bugbot caught
    // an earlier version that silently overrode "all" to
    // "auto_pending_review", producing a button claiming to add 5000
    // rows but only adding ~200. The bulk-approve endpoint already
    // filters server-side (rows not in auto_pending_review come back
    // in `skipped` with a clear reason), so including non-approvable
    // statuses in the selection is safe — they're skipped on approve.
    setSubmitting(true);
    try {
      const result = await dispatch(fetchReviewQueueCaptionIds(statusFilter));
      const ids = result.ids ?? [];
      if (ids.length === 0) {
        Swal.fire({ icon: "info", title: "Nothing to select", text: "No matching rows.", timer: 1200 });
        return;
      }
      setSelected(prev => {
        const next = new Set(prev);
        ids.forEach(id => next.add(id));
        return next;
      });
      if (result.totalCount > result.requestedLimit) {
        // Soft warning — we capped at requestedLimit so the operator
        // doesn't accidentally try to approve 50k rows in one click.
        Swal.fire({
          icon: "warning",
          title: `Selected first ${ids.length} rows`,
          text: `${result.totalCount} match the filter — only the first ${result.requestedLimit} were added to the selection. Approve these, then click again.`,
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onBulkApprove = async () => {
    if (selected.size === 0) return;
    const captionIds = Array.from(selected);
    const { isConfirmed } = await Swal.fire({
      icon: "question",
      title: `Approve ${captionIds.length} caption${captionIds.length === 1 ? "" : "s"} as-is?`,
      text: "Only rows currently in 'auto_pending_review' will be approved. Others (already-reviewed, processing, failed) are skipped.",
      showCancelButton: true,
      confirmButtonText: "Approve",
    });
    if (!isConfirmed) return;
    setSubmitting(true);
    try {
      const result = await dispatch(bulkApproveCaptions(captionIds));
      clearSelection();
      // Refetch the current view so approved rows fall off the
      // Awaiting-Review filter (or change colour under All).
      dispatch(
        fetchCaptionsReviewQueue({
          status: statusFilter,
          page: page + 1,
          per_page: perPage,
        })
      );
      Swal.fire({
        icon: (result?.skipped ?? 0) === 0 ? "success" : "warning",
        title: `Approved ${result?.approved ?? 0}`,
        text:
          (result?.skipped ?? 0) === 0
            ? "Each approved caption is now servable to players."
            : `${result.skipped} skipped — they weren't in 'auto_pending_review' anymore (already approved, processing, or failed since you ticked them).`,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading && rows.length === 0) {
    return <LoadingBackdrop open />;
  }

  return (
    <MainContentContainer>
      <AppBar position="sticky">
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Captions Review Queue
          </Typography>
        </Toolbar>
        {/* Selection bar — same pattern as Generate Captions, kept
            inside the AppBar so it stays sticky. */}
        {selected.size > 0 && (
          <Toolbar sx={{ backgroundColor: "primary.dark", minHeight: 48, gap: 1.5 }}>
            <Typography sx={{ flexGrow: 1, color: "inherit" }} variant="body2">
              Selected: <strong>{selected.size}</strong> caption{selected.size === 1 ? "" : "s"}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={clearSelection}
              sx={{ color: "common.white", borderColor: "rgba(255,255,255,0.5)" }}
            >
              Clear
            </Button>
            <Button
              startIcon={<CheckCircle />}
              variant="contained"
              color="success"
              disabled={submitting}
              onClick={onBulkApprove}
            >
              Approve {selected.size} selected
            </Button>
          </Toolbar>
        )}
      </AppBar>

      {/* Filter bar — rendered in default (light) palette so the Select
          doesn't fight the AppBar's white-on-primary color context. */}
      <Toolbar
        sx={{
          backgroundColor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
          gap: 2,
          py: 1.5,
        }}
      >
        <FormControl size="small" sx={{ minWidth: 280 }}>
          <InputLabel id="status-filter-label">Status</InputLabel>
          <Select
            labelId="status-filter-label"
            value={statusFilter}
            label="Status"
            onChange={e => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
          >
            {STATUS_OPTIONS.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Tooltip title="Adds every auto_pending_review caption matching the current status filter (across all pages) to the selection. Then click Approve to bulk-approve them in one request.">
          <span>
            <Button
              size="small"
              variant="outlined"
              disabled={submitting || totalCount === 0}
              onClick={onSelectAllMatching}
            >
              Select all {totalCount} matching
            </Button>
          </span>
        </Tooltip>
      </Toolbar>

      <Box sx={{ py: 2 }}>
        <TableContainer component={Paper} sx={{ marginTop: 2, boxShadow: 2, borderRadius: 1, overflow: "hidden" }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell padding="checkbox">
                  <Tooltip title="Select / deselect every auto_pending_review row on this page.">
                    <Checkbox
                      indeterminate={approvableSelectedCount > 0 && approvableSelectedCount < approvableRows.length}
                      checked={approvableRows.length > 0 && approvableSelectedCount === approvableRows.length}
                      onChange={e => toggleAllOnPage(e.target.checked)}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <strong>Session</strong>
                </TableCell>
                <TableCell>
                  <strong>Lang</strong>
                </TableCell>
                <TableCell>
                  <strong>Status</strong>
                </TableCell>
                <TableCell>
                  <strong>Source</strong>
                </TableCell>
                <TableCell>
                  <strong>Updated</strong>
                </TableCell>
                <TableCell>
                  <strong>Flags</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>Actions</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="subtitle1" color="text.secondary">
                      No captions match the current filter.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map(row => {
                  const checkable = row.status === "auto_pending_review";
                  return (
                    <TableRow
                      key={row.session_caption_id}
                      selected={isRowSelected(row.session_caption_id)}
                      sx={{ "&:hover": { backgroundColor: "#fafafa" } }}
                    >
                      <TableCell padding="checkbox">
                        <Tooltip
                          title={
                            checkable
                              ? ""
                              : "Only auto_pending_review rows can be bulk-approved. Other statuses can still be reviewed individually."
                          }
                        >
                          <span>
                            <Checkbox
                              disabled={!checkable}
                              checked={isRowSelected(row.session_caption_id)}
                              onChange={() => toggleRow(row.session_caption_id)}
                            />
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">#{row.session_id}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{row.language_code}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={row.status} color={STATUS_COLOR[row.status] || "default"} size="small" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {prettySource(row.source)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {/* Last activity (re-cue / edit / upload / approve),
                            not just the original transcription — tooltip
                            keeps the per-event breakdown. */}
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          title={[
                            row.generated_at ? `Generated ${new Date(row.generated_at).toLocaleString()}` : null,
                            row.recued_at ? `Re-cued ${new Date(row.recued_at).toLocaleString()}` : null,
                            row.reviewed_at ? `Reviewed ${new Date(row.reviewed_at).toLocaleString()}` : null,
                          ].filter(Boolean).join("\n") || undefined}
                        >
                          {row.updated_at ? new Date(row.updated_at).toLocaleString() : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {(row.flags ?? []).slice(0, 4).map(flag => (
                            <Tooltip key={flag.id} title={flag.flag_type} placement="top">
                              <Chip
                                label={flag.flag_type.replace(/_/g, " ")}
                                color={SEVERITY_COLOR[flag.severity] || "default"}
                                size="small"
                                variant="outlined"
                              />
                            </Tooltip>
                          ))}
                          {(row.flags?.length ?? 0) > 4 && (
                            <Chip label={`+${row.flags.length - 4}`} size="small" variant="outlined" />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          component={Link}
                          to={`/captions/edit/${row.session_id}/${row.language_code}?source=${row.source}`}
                          startIcon={<OpenInNew />}
                          size="small"
                          variant="outlined"
                        >
                          Review
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={perPage}
            onRowsPerPageChange={e => {
              setPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </TableContainer>
      </Box>
    </MainContentContainer>
  );
};

export default CaptionsReviewQueuePage;
